import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronUp, Check, Circle, Clock, Edit3 } from 'lucide-react';
import { ProcessStageDto, ProcessStageStatus, CommitteeDto, CommitteeDecisionStatus } from '../../services/committeesService';
import { hexToRgba } from '../../utils/colorUtils';

interface StageAccordionProps {
  stages: ProcessStageDto[];
  committee: CommitteeDto | null;
  onEditStage?: (stage: ProcessStageDto) => void;
}

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  direction: rtl;
`;

const AccordionItem = styled.div<{ $status: ProcessStageStatus }>`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-right: 4px solid ${props => {
    switch (props.$status) {
      case ProcessStageStatus.Completed:
        return '#16a34a';
      case ProcessStageStatus.Current:
        return 'var(--primary-color)';
      default:
        return 'var(--border-color)';
    }
  }};
  border-radius: var(--border-radius-md);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: var(--shadow-light);
  }
`;

const AccordionHeader = styled.button<{ $isExpanded: boolean; $status: ProcessStageStatus }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--surface-color);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: right;

  &:hover {
    background: var(--background-secondary);
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusIcon = styled.div<{ $status: ProcessStageStatus }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.$status) {
      case ProcessStageStatus.Completed:
        return hexToRgba('#16a34a', 0.15);
      case ProcessStageStatus.Current:
        return hexToRgba('#0073ea', 0.15);
      default:
        return 'var(--background-secondary)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case ProcessStageStatus.Completed:
        return '#16a34a';
      case ProcessStageStatus.Current:
        return 'var(--primary-color)';
      default:
        return 'var(--text-tertiary)';
    }
  }};
`;

const StageName = styled.span<{ $status: ProcessStageStatus }>`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.$status === ProcessStageStatus.Pending ? 'var(--text-secondary)' : 'var(--text-primary)'};
`;

const StatusBadge = styled.span<{ $color: string }>`
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
  background: ${props => hexToRgba(props.$color, 0.12)};
  color: ${props => props.$color};
`;

const DateText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ExpandIcon = styled.span<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
`;

const AccordionContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ContentInner = styled.div`
  padding: 20px;
  border-top: 1px solid var(--border-color);
  background: var(--background-color);
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FieldValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const HighlightValue = styled(FieldValue)`
  font-size: 18px;
  font-weight: 700;
  color: #16a34a;
`;

const Section = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;

  &:hover {
    background: var(--primary-light); color: var(--primary-color);
  }
`;

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusBadgeColor = (status: ProcessStageStatus): string => {
  switch (status) {
    case ProcessStageStatus.Completed:
      return '#16a34a';
    case ProcessStageStatus.Current:
      return '#0073ea';
    default:
      return '#676879';
  }
};

const getStatusText = (status: ProcessStageStatus): string => {
  switch (status) {
    case ProcessStageStatus.Completed:
      return 'הושלם';
    case ProcessStageStatus.Current:
      return 'בתהליך';
    default:
      return 'עתידי';
  }
};

const getStatusIcon = (status: ProcessStageStatus): React.ReactNode => {
  switch (status) {
    case ProcessStageStatus.Completed:
      return <Check size={16} />;
    case ProcessStageStatus.Current:
      return <Clock size={16} />;
    default:
      return <Circle size={16} />;
  }
};

const StageAccordion: React.FC<StageAccordionProps> = ({
  stages,
  committee,
  onEditStage
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Auto-expand current stage
  useEffect(() => {
    const currentStage = stages.find(s => s.status === ProcessStageStatus.Current);
    if (currentStage) {
      setExpandedIds(new Set([currentStage.id]));
    }
  }, [stages]);

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const renderCommitteeDetails = () => {
    if (!committee) {
      return (
        <FieldsGrid>
          <Field>
            <FieldValue>טרם נקבעה ועדה</FieldValue>
          </Field>
        </FieldsGrid>
      );
    }

    return (
      <>
        <FieldsGrid>
          <Field>
            <FieldLabel>מספר הערכה</FieldLabel>
            <FieldValue>{committee.evaluationNumber} מתוך 3</FieldValue>
          </Field>
          <Field>
            <FieldLabel>תאריך ושעה</FieldLabel>
            <FieldValue>{formatDateTime(committee.committeeDate)}</FieldValue>
          </Field>
          {committee.location && (
            <Field>
              <FieldLabel>מיקום</FieldLabel>
              <FieldValue>{committee.location}</FieldValue>
            </Field>
          )}
          <Field>
            <FieldLabel>ליווי</FieldLabel>
            <FieldValue>
              {committee.escortNeeded ? `כן - ${committee.escortType || 'לא צוין'}` : 'לא'}
            </FieldValue>
          </Field>
        </FieldsGrid>

        {(committee.usesLawyer || committee.requiresMedicalOpinion) && (
          <Section>
            <SectionTitle>שירותים נוספים</SectionTitle>
            <FieldsGrid>
              {committee.usesLawyer && (
                <Field>
                  <FieldLabel>עורך דין</FieldLabel>
                  <FieldValue>{committee.lawyerName || 'לא צוין שם'}</FieldValue>
                </Field>
              )}
              {committee.requiresMedicalOpinion && (
                <Field>
                  <FieldLabel>חוות דעת רפואית</FieldLabel>
                  <FieldValue>{committee.medicalOpinionDetails || 'נדרשת'}</FieldValue>
                </Field>
              )}
            </FieldsGrid>
          </Section>
        )}

        {committee.notes && (
          <Section>
            <SectionTitle>הערות</SectionTitle>
            <FieldValue style={{ whiteSpace: 'pre-wrap' }}>{committee.notes}</FieldValue>
          </Section>
        )}
      </>
    );
  };

  const renderDecisionSummary = () => {
    if (!committee) {
      return (
        <FieldsGrid>
          <Field>
            <FieldValue style={{ color: 'var(--text-secondary)' }}>אין נתוני החלטה</FieldValue>
          </Field>
        </FieldsGrid>
      );
    }

    const isApproved = committee.decisionStatus === CommitteeDecisionStatus.ApprovedPermanent ||
                       committee.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod;

    return (
      <FieldsGrid>
        <Field>
          <FieldLabel>סטטוס</FieldLabel>
          <StatusBadge $color={isApproved ? '#16a34a' : '#dc2626'}>
            {committee.decisionStatusName}
          </StatusBadge>
        </Field>
        {isApproved && committee.disabilityPercentage && (
          <Field>
            <FieldLabel>אחוז נכות</FieldLabel>
            <HighlightValue>{committee.disabilityPercentage}%</HighlightValue>
          </Field>
        )}
        {committee.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod &&
         committee.approvalEndDate && (
          <Field>
            <FieldLabel>תאריך סיום זכאות</FieldLabel>
            <FieldValue>{formatDate(committee.approvalEndDate)}</FieldValue>
          </Field>
        )}
      </FieldsGrid>
    );
  };

  const renderStageContent = (stage: ProcessStageDto) => {
    // Check if this is a committee-related stage
    const isCommitteeStage = stage.stageName.includes('וועדה') || stage.stageName.includes('ממתין');
    const isDecisionStage = stage.stageName.includes('אושר') || stage.stageName.includes('נדחה');

    if (isCommitteeStage) {
      return renderCommitteeDetails();
    }

    // For decision stages (אושר/נדחה), show only summary - full details are in committee history
    if (isDecisionStage) {
      return renderDecisionSummary();
    }

    // Generic stage content
    return (
      <FieldsGrid>
        {stage.stageDate && (
          <Field>
            <FieldLabel>תאריך</FieldLabel>
            <FieldValue>{formatDate(stage.stageDate)}</FieldValue>
          </Field>
        )}
        {stage.description && (
          <Field>
            <FieldLabel>תיאור</FieldLabel>
            <FieldValue>{stage.description}</FieldValue>
          </Field>
        )}
        {!stage.stageDate && !stage.description && (
          <Field>
            <FieldValue style={{ color: 'var(--text-secondary)' }}>אין מידע נוסף</FieldValue>
          </Field>
        )}
      </FieldsGrid>
    );
  };

  return (
    <AccordionContainer>
      {stages.map(stage => {
        const isExpanded = expandedIds.has(stage.id);
        const badgeColor = getStatusBadgeColor(stage.status);

        return (
          <AccordionItem key={stage.id} $status={stage.status}>
            <AccordionHeader
              $isExpanded={isExpanded}
              $status={stage.status}
              onClick={() => toggleExpand(stage.id)}
            >
              <HeaderLeft>
                <StatusIcon $status={stage.status}>
                  {getStatusIcon(stage.status)}
                </StatusIcon>
                <StageName $status={stage.status}>{stage.stageName}</StageName>
              </HeaderLeft>
              <HeaderRight>
                <StatusBadge $color={badgeColor}>
                  {getStatusText(stage.status)}
                </StatusBadge>
                {stage.stageDate && (
                  <DateText>{formatDate(stage.stageDate)}</DateText>
                )}
                <ExpandIcon $isExpanded={isExpanded}>
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </ExpandIcon>
              </HeaderRight>
            </AccordionHeader>

            <AccordionContent $isExpanded={isExpanded}>
              <ContentInner>
                {renderStageContent(stage)}
                {onEditStage && stage.status === ProcessStageStatus.Current && (
                  <ActionButton onClick={() => onEditStage(stage)}>
                    <Edit3 size={16} />
                    ערוך שלב
                  </ActionButton>
                )}
              </ContentInner>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </AccordionContainer>
  );
};

export default StageAccordion;
